import type { Dispatch, SetStateAction } from "react";
import {
  clearBackdropCalibration,
  writeBackdropCalibration,
  type BackdropCalibration,
} from "../model/inp/backdrop.ts";
import { writeProjectOptions, type ProjectOptionsForm } from "../model/inp/options.ts";
import { applyCoordinateTransform } from "../model/inp/georeference.ts";
import { toLonLat } from "../model/geo/reproject.ts";
import { WGS84_PROJ4 } from "../model/geo/crs.ts";
import type { SimilarityFit } from "../model/geo/controlPointFit.ts";
import type { Point } from "../model/inp/types.ts";
import type { StoredProject } from "../model/storage.ts";

interface UseProjectDataActionsOptions {
  loaded: StoredProject | null;
  setLoaded: Dispatch<SetStateAction<StoredProject | null>>;
  mutateRawUndoable: (
    fn: (raw: ReadonlyMap<string, string[][]>) => ReadonlyMap<string, string[][]>
  ) => void;
  setPlaceholderElements: Dispatch<SetStateAction<Set<string>>>;
  setMapMode: (mode: "plane" | "geo") => void;
  closeGeoreferenceDialog: () => void;
  /** Called only when `applyGeoreference` actually drops one or more rows (a non-finite input or out-of-range transform result) — App.tsx owns the resulting AlertDialog, same pattern as `reportStorageError`/`storageError`. */
  reportGeoreferenceWarnings: (warnings: string[]) => void;
}

/** Every "apply an external dialog's own result to the project" action — OptionsDialog, ProjectDataDialog, BackgroundImageDialog, GisImportDialog, GeoreferenceDialog each hand back their own result shape, but all of them funnel through `mutateRawUndoable`/`setLoaded`. */
export function useProjectDataActions(opts: UseProjectDataActionsOptions) {
  function saveOptions(form: ProjectOptionsForm) {
    opts.mutateRawUndoable((raw) => writeProjectOptions(raw, form));
  }

  /** `ProjectDataDialog`'s Save button hands back the whole rawSections it already rewrote — a single atomic action, so a plain `mutateRawUndoable` push needs no coalescing, unlike PropertyPanel's live-typing fields. */
  function saveProjectData(raw: ReadonlyMap<string, string[][]>) {
    opts.mutateRawUndoable(() => raw);
  }

  /**
   * The blob goes straight into `loaded` (the debounced autosave effect
   * picks it up automatically); the calibration goes through the normal
   * undoable raw mutation path. `calibration === null` (the dialog's
   * "Remove image" action) clears both.
   */
  function saveBackgroundImage(blob: Blob | null, calibration: BackdropCalibration | null) {
    if (!opts.loaded) return;
    opts.setLoaded((current) => (current ? { ...current, backgroundImageBlob: blob } : current));
    if (calibration) opts.mutateRawUndoable((raw) => writeBackdropCalibration(raw, calibration));
    else opts.mutateRawUndoable((raw) => clearBackdropCalibration(raw));
  }

  /**
   * Reprojection into the right target CRS, and whether an empty project
   * should ADOPT a CRS from the layers it's importing, both happen upstream
   * (`GisImportDialog`'s `handleImport`, via `resolveImportTargetCrs`) —
   * every row in `next` is already written in the project's final CRS by
   * the time this runs. This function's only job is applying an
   * ALREADY-DECIDED adopted CRS, only ever set when the project was
   * genuinely empty before the import.
   */
  function importLayers(
    next: ReadonlyMap<string, string[][]>,
    placeholderNames: string[],
    adoptedCrs?: string
  ) {
    opts.mutateRawUndoable(() => next);
    if (placeholderNames.length > 0) {
      opts.setPlaceholderElements((current) => new Set([...current, ...placeholderNames]));
    }
    if (adoptedCrs) {
      opts.setLoaded((current) => (current ? { ...current, crs: adoptedCrs } : current));
      opts.setMapMode("geo");
    }
  }

  /**
   * `fit` maps a local-plane point into `tangentPlaneCrs`'s scratch tangent-plane meters; this converts that one more step, into real WGS84 degrees, before rewriting every project coordinate through it.
   *
   * The transform runs HERE, synchronously, against `opts.loaded.rawSections`
   * directly — not inside the `mutateRawUndoable` callback the way this used
   * to. React's `setState` updater form doesn't run its callback
   * synchronously, so checking `warnings.size` right after calling
   * `mutateRawUndoable` (the old code) almost always saw `0` even when rows
   * genuinely got dropped — a coordinate-transform failure was silently
   * invisible even to `console.warn`, let alone the user. Computing `next`
   * up front makes `warnings` fully populated before anything reads it.
   */
  function applyGeoreference(fit: SimilarityFit, tangentPlaneCrs: string) {
    if (!opts.loaded) return;
    function toWgs84(p: Point): Point {
      const [lon, lat] = toLonLat(fit.apply(p), tangentPlaneCrs);
      return { x: lon, y: lat };
    }
    const warnings = new Set<string>();
    const next = applyCoordinateTransform(opts.loaded.rawSections, toWgs84, warnings);
    opts.mutateRawUndoable(() => next);
    if (warnings.size > 0) opts.reportGeoreferenceWarnings([...warnings]);
    opts.setLoaded((current) => (current ? { ...current, crs: WGS84_PROJ4 } : current));
    opts.setMapMode("geo");
    opts.closeGeoreferenceDialog();
  }

  return { saveOptions, saveProjectData, saveBackgroundImage, importLayers, applyGeoreference };
}
